import React from 'react';
import { ModuleBase, PreHeading, ResponsiveImage, Link, ThreeDNotice } from 'components';
import External from 'assets/externalLink.svg';
import Phone from 'assets/phone.svg';
import Mail from 'assets/mail.svg';
import { isColorDark } from '@/utils';
import classNames from 'classnames';
import { useRouter } from 'next/router';

const MapSimpleModule = ({ data }) => {
  const router = useRouter();
  const cajaRouter = router.asPath.includes('caja-by-maxx-royal/caja-landing');

  return (
    <ModuleBase data={data}>
      <div
        className={classNames(
          'container flex flex-col items-center lg:justify-between md:items-start lg:items-center gap-8 md:gap-16 font-montserrat',
          data.alignment === 'Left' ? 'lg:flex-row' : 'lg:flex-row-reverse',
        )}
      >
        {data.mapImage && (
          <div className="w-full lg:max-w-[600px]">
            <div className="w-full h-[298px] md:h-[600px]">
              <ResponsiveImage
                image={data.mapImage}
                widths={{ xs: 1026, md: 2064, lg: 1800 }}
                heights={{ xs: 894, md: 1800, lg: 1800 }}
              />
            </div>
            <ThreeDNotice className="mt-2" />
          </div>
        )}
        <div className="w-full lg:max-w-1/2 lg:p-10 font-montserrat font-normal">
          <div className="location-info">
            {data.locationLabel && (
              <PreHeading
                className="color-from-bg text-base leading-5 tracking-headline-m-tablet font-semibold uppercase md:text-xl md:leading-6 md:tracking-title-m"
                aria-label="Location"
                role="heading"
                aria-level="3"
                aria-describedby="Title for the map"
              >
                {data.locationLabel}
              </PreHeading>
            )}
            {data.location && (
              <div className="color-from-bg text-smleading-6 md:text-base md:leading-6.5 my-4 md:max-w-[340px] !text-opacity-75">
                {data.location}
              </div>
            )}
            {data.primaryCta && (
              <Link
                link={data.primaryCta}
                className={classNames(
                  'font-semibold text-sm py-2 leading-6 flex items-center gap-2',
                  isColorDark(data.backgroundColour)
                    ? '[&_svg>path]:stroke-brand-soft-white'
                    : '[&_svg>path]:stroke-brand-black',
                )}
              >
                <p className="color-from-bg">{data.primaryCta.text}</p>
                <External role="presentation" />
              </Link>
            )}
          </div>
          <div className="info mt-10 md:mt-16">
            {data.infoTitle && (
              <PreHeading className="color-from-bg font-semibold text-sm leading-normal uppercase tracking-title-s">
                {data.infoTitle}
              </PreHeading>
            )}
            {data.infoText && (
              <div className="color-from-bg text-sm leading-6 md:text-base md:leading-6.5 my-6 !text-opacity-75">
                {data.infoText}
              </div>
            )}
            {(data.phoneNumber || data.emailAddress) && (
              <div className="contact-wrapper">
                {data.phoneNumber && (
                  <a
                    href={`tel:${data.phoneNumber?.text}`}
                    className={classNames(
                      'flex items-center text-sm leading-6 gap-3 mb-6',
                      isColorDark(data.backgroundColour)
                        ? '[&_svg>path]:stroke-brand-soft-white'
                        : '[&_svg>path]:stroke-brand-black',
                    )}
                  >
                    <Phone role="presentation" />
                    <p
                      className={classNames(
                        'border-b',
                        isColorDark(data.backgroundColour)
                          ? 'text-brand-soft-white border-brand-soft-white'
                          : 'text-brand-black border-brand-black',
                      )}
                    >
                      {data.phoneNumber.text}
                    </p>
                  </a>
                )}
                {cajaRouter && (
                  <div className={classNames('flex items-center text-sm leading-6 gap-3 mb-6')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                      <g id="SVGRepo_iconCarrier">
                        {' '}
                        <path
                          d="M6.014 8.00613C6.12827 7.1024 7.30277 5.87414 8.23488 6.01043L8.23339 6.00894C9.14051 6.18132 9.85859 7.74261 10.2635 8.44465C10.5504 8.95402 10.3641 9.4701 10.0965 9.68787C9.7355 9.97883 9.17099 10.3803 9.28943 10.7834C9.5 11.5 12 14 13.2296 14.7107C13.695 14.9797 14.0325 14.2702 14.3207 13.9067C14.5301 13.6271 15.0466 13.46 15.5548 13.736C16.3138 14.178 17.0288 14.6917 17.69 15.27C18.0202 15.546 18.0977 15.9539 17.8689 16.385C17.4659 17.1443 16.3003 18.1456 15.4542 17.9421C13.9764 17.5868 8 15.27 6.08033 8.55801C5.97237 8.24048 5.99955 8.12044 6.014 8.00613Z"
                          fill="#000"
                        ></path>{' '}
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12 23C10.7764 23 10.0994 22.8687 9 22.5L6.89443 23.5528C5.56462 24.2177 4 23.2507 4 21.7639V19.5C1.84655 17.492 1 15.1767 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM6 18.6303L5.36395 18.0372C3.69087 16.4772 3 14.7331 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C11.0143 21 10.552 20.911 9.63595 20.6038L8.84847 20.3397L6 21.7639V18.6303Z"
                          fill="#000"
                        ></path>{' '}
                      </g>
                    </svg>
                    <p
                      className={classNames(
                        'border-b',
                        isColorDark(data.backgroundColour)
                          ? 'text-brand-soft-white border-brand-soft-white'
                          : 'text-brand-black border-brand-black',
                      )}
                    >
                      +90 538 211 58 11
                    </p>
                  </div>
                )}
                {data.emailAddress && (
                  <a
                    href={`mailto:${data.emailAddress?.text}`}
                    className={classNames(
                      'flex items-center text-sm leading-6 gap-3',
                      isColorDark(data.backgroundColour)
                        ? '[&_svg>path]:stroke-brand-soft-white'
                        : '[&_svg>path]:stroke-brand-black',
                    )}
                  >
                    <Mail role="presentation" />
                    <p
                      className={classNames(
                        'border-b',
                        isColorDark(data.backgroundColour)
                          ? 'text-brand-soft-white border-brand-soft-white'
                          : 'text-brand-black border-brand-black',
                      )}
                    >
                      {data.emailAddress.text}
                    </p>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default MapSimpleModule;
